import { useEffect, useState } from 'react'
import { ChevronRight, Loader2, MapPin, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { listUnits } from '@/features/geography/api'
import { explain } from '@/shared/api/problem'
import type { UnitResponse } from '@/shared/api/types'

/**
 * County → Constituency → Ward → Polling station.
 *
 * Every level sets the **same** query param, `unitId`, because the backend
 * resolves any unit to its `ltree` path and filters `unit_path <@ path`. A
 * county and a station are the same predicate at different depths, so there is
 * one param, not four, and no way for them to disagree.
 *
 * Picking a level clears the levels below it — choosing a different constituency
 * must not leave the previous ward's filter applied, which is the classic
 * cascading-select bug and produces an empty table nobody can explain.
 */

// English plurals are irregular enough that `label + 's'` produces
// "All constituencys". Both forms are spelled out.
const LEVELS = [
  { type: 'COUNTY', label: 'County', plural: 'counties' },
  { type: 'CONSTITUENCY', label: 'Constituency', plural: 'constituencies' },
  { type: 'WARD', label: 'Ward', plural: 'wards' },
  { type: 'POLLING_STATION', label: 'Station', plural: 'stations' },
] as const

export interface CascadeValue {
  /** The deepest unit chosen. This is what goes to the API. */
  unitId?: string
  /** Chosen unit per level, so the selects can be repopulated from a URL. */
  path: { id: string; name: string; type: string }[]
}

export function UnitCascade({
  value, onChange, className, required,
}: {
  value: CascadeValue
  onChange: (next: CascadeValue) => void
  className?: string
  /**
   * The cascade is a FILTER on the voters page, where "All counties" is a real
   * and useful state. On a form it is a required field, where that placeholder
   * reads as a chosen value rather than an empty one.
   */
  required?: boolean
}) {
  // Depth shown = chosen levels + 1, capped at four. A user who has picked a
  // county sees the constituency select appear; nobody is asked to fill in four
  // dropdowns before seeing a row.
  const depth = Math.min(value.path.length + 1, LEVELS.length)

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5 pb-4', className)}>
      <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      {LEVELS.slice(0, depth).map((level, i) => (
        <div key={level.type} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />}
          <LevelSelect
            label={level.label}
            plural={level.plural}
            type={level.type}
            parentId={i === 0 ? undefined : value.path[i - 1]?.id}
            required={required}
            selected={value.path[i]?.id ?? ''}
            onPick={(unit) => {
              // Truncate at this level, then append — deeper choices are void.
              const next = value.path.slice(0, i)
              if (unit) next.push({ id: unit.id, name: unit.name, type: level.type })
              onChange({ unitId: next[next.length - 1]?.id, path: next })
            }}
          />
        </div>
      ))}
      {value.path.length > 0 && (
        <button
          type="button"
          onClick={() => onChange({ unitId: undefined, path: [] })}
          className="h-9 px-2 inline-flex items-center gap-1 text-erp-xs text-muted-foreground hover:text-foreground"
          aria-label="Clear the geography filter"
        >
          <X className="h-3 w-3" /> Clear
        </button>
      )}
    </div>
  )
}

/**
 * `UnitQueryService.list` throws `BadRequestException("size must be between 1
 * and 200")`. This component asked for 300, so EVERY level 400'd, the `.catch`
 * turned that into an empty array, and every select rendered "All counties" and
 * disabled — on the voters filter bar and, once it became a required field on
 * the beneficiary enrol form, on a control somebody had to use to submit.
 *
 * 200 is comfortably enough at every level once a parent is chosen: 47 counties,
 * about 6 constituencies to a county, 5 wards to a constituency, 30 stations to
 * a ward. The unparented deep levels are refused before the request is built.
 */
const MAX_UNITS = 200

function LevelSelect({
  label, plural, type, parentId, selected, onPick, required,
}: {
  label: string
  plural: string
  type: string
  parentId?: string
  selected: string
  onPick: (unit: UnitResponse | null) => void
  /** Changes the placeholder from a filter's "All wards" to a form's "Choose a ward". */
  required?: boolean
}) {
  const [units, setUnits] = useState<UnitResponse[]>([])
  const [loading, setLoading] = useState(false)
  // A failed request is NOT an empty list. Swallowing the 400 into `[]` is what
  // made a broken control look like an empty country.
  const [error, setError] = useState<string | null>(null)
  const [truncated, setTruncated] = useState(false)

  useEffect(() => {
    // A county list needs no parent; every deeper level does. Asking for wards
    // with no constituency would return every ward in the country.
    if (type !== 'COUNTY' && !parentId) { setUnits([]); setError(null); return }
    let cancelled = false
    setLoading(true)
    setError(null)
    listUnits({ type: [type], parentId: parentId ?? '' } as never, 0, MAX_UNITS)
      .then((page) => {
        if (cancelled) return
        setUnits(page.content)
        setTruncated((page.totalElements ?? page.content.length) > page.content.length)
      })
      .catch((e) => { if (!cancelled) { setUnits([]); setError(explain(e)) } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [type, parentId])

  return (
    <div className="relative">
      <select
        value={selected}
        // Disabled only while LOADING. An empty list stays enabled and says why —
        // a dead control with no explanation is unactionable, and this one was
        // dead for a reason nothing on screen could have told anybody.
        disabled={loading}
        onChange={(e) => onPick(units.find((u) => u.id === e.target.value) ?? null)}
        className={cn(
          'h-9 pl-2.5 pr-7 border rounded-md bg-background text-erp-sm appearance-none',
          'disabled:opacity-50 min-w-[8.5rem] max-w-[12rem]',
          selected ? 'border-chocolate/50 text-chocolate font-medium' : 'border-input',
          error && 'border-red-400',
        )}
        aria-label={label}
        title={error ?? undefined}
      >
        <option value="">
          {loading ? `${label}…`
            : error ? `${label} — could not load`
            : units.length === 0 ? `No ${plural} here`
            : required ? `Choose a ${label.toLowerCase()}`
            : `All ${plural}`}
        </option>
        {units.map((u) => (
          <option key={u.id} value={u.id}>{u.name}</option>
        ))}
      </select>
      {loading && (
        <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-3 text-muted-foreground pointer-events-none" />
      )}
      {/* Named where it happened, not folded into a page-level strip: which of the
          four levels failed is the whole diagnosis. */}
      {error && (
        <p className="absolute left-0 top-full mt-0.5 text-erp-xs text-red-700 dark:text-red-300 whitespace-nowrap">
          {error}
        </p>
      )}
      {truncated && !error && (
        <p className="absolute left-0 top-full mt-0.5 text-erp-xs text-muted-foreground whitespace-nowrap">
          First {MAX_UNITS} only
        </p>
      )}
    </div>
  )
}
